<template>
    <div class="checkout-flight-pax">
        <div class="checkout-flight-pax-header">
            <div class="checkout-flight-pax-header-title">
                <Translation
                    element="strong"
                    :translate="{
                        key: 'shared.traveller',
                        format: LabelFormat.Capitalize,
                    }" />
            </div>
            <div class="checkout-flight-pax-header-type">
                {{ travellerType }}
            </div>
        </div>

        <div class="checkout-flight-pax-fields">
            <div v-if="gender" class="checkout-flight-pax-fields-genders">
                <div class="checkout-flight-pax-fields-genders-label">
                    {{ t({ key: 'shared.gender' }) }}
                </div>
                <RadioField
                    v-model="pax.Gender"
                    :name="gender.name"
                    :input="{ items: genderItems, inline: true }" />
            </div>

            <TextboxField
                v-if="firstName"
                v-model="pax.FirstName"
                :name="firstName.name"
                :input="{
                    label: t({
                        key: 'checkout.first_name_placeholder_label',
                    }),
                    capitalize: true,
                    autocomplete: 'given-name',
                }" />
            <TextboxField
                v-if="lastName"
                v-model="pax.LastName"
                :name="lastName.name"
                :input="{
                    label: t({
                        key: 'checkout.last_name_placeholder_label',
                    }),
                    capitalize: true,
                    autocomplete: 'family-name',
                }" />
            <DateInputField
                v-if="dateOfBirth"
                v-model="pax.DateOfBirth"
                :name="dateOfBirth.name"
                :input="{
                    label: t({ key: 'shared.date_of_birth' }),
                    autocomplete: 'bday',
                }" />

            <SelectField
                v-if="nationality"
                v-model="pax.Nationality"
                :name="nationality.name"
                :input="{
                    label: t({ key: 'shared.nationality' }),
                    items: nationalityItems,
                }" />
        </div>

        <div v-if="showBaggage" class="checkout-flight-pax-baggage">
            <div class="checkout-flight-pax-baggage-title">
                {{ t({ key: 'shared.choose_baggage' }) }}
            </div>
            <Baggage
                :id="id"
                :paxIndex="index"
                :product="product"
                :additionalBaggage="additionalBaggage"
                :included="bagsIncluded"
                smallBagIncluded />
        </div>
        <BaggageNone v-else />

        <Divider v-if="showDivider" color="lightGrey" thickness="1" topMargin="xxl" />
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType } from 'vue';
    import {
        FlightPax,
        FlightProduct,
        MandatoryField,
        Nationality,
    } from '@/api/Cart/cart.interfaces';
    import { AdditionalBaggage } from '@shared/interfaces/baggage';
    import { filterAdditionalBaggage, getIncludedBaggages } from '@shared/helpers/baggage';
    import { getAgeRange } from '@/api/Cart/cart.helpers';
    import { useStore } from '@/store';
    import { AgeRangeDefinitions, PassengerType } from '@/api/Checkout/checkout.interfaces';
    import { LabelFormat } from '@shared/interfaces/translation';
    import { configBase } from '@/config/base';
    import { SelectItem } from '@ui/components/FormV2/Shared/interfaces/Select.interfaces';
    import { getPaxSubHeader } from '@/helpers/guests';
    import { getGendersByProductAndPax } from '@/helpers/checkout';
    import { getCapitalizedCase } from '@shared/utils/string';
    import Baggage from '../Baggage/Baggage.vue';
    import BaggageNone from '@ui/components/BaggageSelectionForm/BaggageNone.vue';
    import RadioField from '@ui/components/FormV2/Fields/Radio/RadioField.vue';
    import TextboxField from '@ui/components/FormV2/Fields/Textbox/TextboxField.vue';
    import DateInputField from '@ui/components/FormV2/Fields/DateInput/DateInputField.vue';
    import SelectField from '@ui/components/FormV2/Fields/Select/SelectField.vue';
    import Translation from '@ui/components/Translation/Translation.vue';
    import Divider from '@ui/components/Divider/Divider.vue';
    import { getPaxFields } from '../FlightPax.helpers';

    export default defineComponent({
        name: 'Pax',
        methods: { t },
        components: {
            SelectField,
            DateInputField,
            TextboxField,
            RadioField,
            Baggage,
            BaggageNone,
            Translation,
            Divider,
        },
        props: {
            id: { type: String, required: true },
            index: { type: Number, required: true },
            pax: { type: Object as PropType<FlightPax>, required: true },
            product: {
                type: Object as PropType<FlightProduct>,
                required: true,
            },
            ageRangeDefinitions: {
                type: Object as PropType<AgeRangeDefinitions>,
                required: true,
            },
            toDate: {
                type: Object as PropType<Date>,
                required: true,
            },
        },
        setup(props) {
            const { smallBagIncluded } = configBase;

            const store = useStore();

            const fields = computed(() => getPaxFields({ ...props }));

            const getField = (field: MandatoryField) =>
                fields.value.find((f) => f.field === field);

            const gender = computed(() => getField(MandatoryField.Gender));

            const firstName = computed(() => getField(MandatoryField.FirstName));

            const lastName = computed(() => getField(MandatoryField.LastName));

            const dateOfBirth = computed(() => getField(MandatoryField.DateOfBirth));

            const nationality = computed(() => getField(MandatoryField.Nationality));

            const ageRangeDefinitions = computed<AgeRangeDefinitions>(() => {
                return store.getters['content/getCheckoutAgeRangeDefinitions'];
            });

            const ageRange = computed(() => {
                return getAgeRange(props.product, props.pax, ageRangeDefinitions.value);
            });

            const isInfant = computed<boolean>(
                () => ageRange.value.Type === PassengerType.Infant
            );

            const showDivider = computed<boolean>(() => {
                return props.index < props.product.Pax.length - 1;
            });

            const showBaggage = computed<boolean>(() => !isInfant.value);

            const genderItems = computed(() =>
                getGendersByProductAndPax(props.product, props.pax)
            );

            const nationalityItems = computed<SelectItem[]>(() => {
                const nationalities: Nationality[] =
                    store.getters['content/getCheckoutNationalities'];

                return nationalities.length
                    ? nationalities.map((nationality) => ({
                          label: nationality.NationalityName,
                          value: nationality,
                      }))
                    : [];
            });

            const travellerType = computed(() => {
                if (props.pax.PaxDescription) {
                    const label = getCapitalizedCase(
                        getPaxSubHeader(
                            ageRangeDefinitions.value,
                            props.product.Type,
                            props.pax.PaxDescription.Age
                        )
                    );
                    return label;
                }

                return undefined;
            });

            const travellerTitle = computed((): string => {
                const traveller = t({
                    key: 'shared.traveller',
                    format: LabelFormat.Capitalize,
                });

                const count = props.index + 1;

                return `${traveller} ${count}`;
            });

            const additionalBaggage = computed<AdditionalBaggage>(() =>
                filterAdditionalBaggage(props.pax.AdditionalBaggage)
            );

            const bagsIncluded = computed(() => getIncludedBaggages(props.product));

            return {
                showDivider,
                showBaggage,
                additionalBaggage,
                bagsIncluded,
                travellerTitle,
                travellerType,
                smallBagIncluded,
                genderItems,
                nationalityItems,
                gender,
                firstName,
                lastName,
                dateOfBirth,
                nationality,
                LabelFormat,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Pax.scss';
</style>
